import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * @typedef {object} UseAsyncReturn
 * @property {function} run the function that can be run to start the async function
 * @property {string} loading true if the function is currently running and false if
 * it either has not been started or has alread finished
 * @property {object} error the error, if any, for the async function
 * @property {object} response the return value of the async function
 */

/**
 * Tracks status of an async function, allowing either an immediate start or a
 * manual trigger via the returned run parameter.
 *
 * @param {function:Promise<any>} asyncFn the async function that needs to be tracked
 * @param {boolean} immediate whether or not to run the function immediately (if false,
 * you must manually call the returned run function to run it)
 * @returns {UseAsyncReturn}
 *
 * @example
 * const { run, loading, error, response } = useAsync(myAsyncFunction);
*/
const useAsync = (asyncFn, immediate = false) => {
  const isMountedRef = useRef(false);

  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  const [response, setResponse] = useState(null);

  const run = useCallback(() => {
    setLoading(true);
    setError(null);
    setResponse(null);

    asyncFn()
      .then((response) => {
        if (isMountedRef.current) {
          setResponse(response);
        }
      })
      .catch((error) => {
        if (isMountedRef.current) {
          console.error(error);
          setError(error);
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setLoading(false);
        }
      });
  }, [asyncFn]);

  useEffect(() => {
    isMountedRef.current = true;

    if (immediate) {
      run();
    }

    return () => {
      /*
        On cleanup, indicate that the state should no longer be updated because
        the parent component is no longer mounted.  This avoids the error "Can't
        perform a react state update on an unmounted component".
      */
      isMountedRef.current = false;
    };
  }, []);

  return useMemo(() => ({ run, loading, error, response }), [run, loading, error, response]);
};

export default useAsync;
